import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();

    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;

    closestSection.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect
    );

    document.addEventListener('change', this.#handleInsuranceChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const closestSection = this.closest('.shopify-section, dialog');

    if (closestSection) {
      closestSection.removeEventListener(
        StandardEvents.productSelect,
        this.#handleProductSelect
      );
    }

    document.removeEventListener('change', this.#handleInsuranceChange);
  }

  #handleInsuranceChange = (event) => {
    const insuranceInput = event.target.closest(
      '[data-insurance-options] input[type="radio"]'
    );

    if (!insuranceInput) return;

    const insurancePrice = Number(
      insuranceInput.dataset.insurancePrice || 0
    );

    const priceContainer = this.refs.priceContainer;
    if (!priceContainer) return;

    const priceElement = priceContainer.querySelector(
      '[data-price], .price, .money'
    );

    if (!priceElement) return;

    if (!this.dataset.basePrice) {
      const currentPrice = Number(
        priceElement.textContent
          .replace(/[^\d,.-]/g, '')
          .replace(/\./g, '')
          .replace(',', '.')
      );

      if (Number.isNaN(currentPrice)) return;

      this.dataset.basePrice = currentPrice;
    }

    const newPrice =
      Number(this.dataset.basePrice) + insurancePrice;

    this.dataset.insurancePrice = insurancePrice;

    priceElement.textContent =
      newPrice.toLocaleString('da-DK', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }) + ' kr.';
  };

  /**
   * Handles product select event and updates the price.
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (
      !(event.target instanceof Element) ||
      event.target.closest('product-card')
    ) {
      return;
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (
          detail.productId &&
          detail.productId !== this.dataset.productId
        ) {
          return;
        }

        const { priceContainer, volumePricingNote } = this.refs;

        const newProductPrice = html.querySelector(
          `product-price[data-block-id="${this.dataset.blockId}"]`
        );

        if (!newProductPrice) return;

        const newPrice = newProductPrice.querySelector(
          '[ref="priceContainer"]'
        );

        if (newPrice && priceContainer) {
          priceContainer.replaceWith(newPrice);
        }

        const newNote = newProductPrice.querySelector(
          '[ref="volumePricingNote"]'
        );

        if (!newNote) {
          volumePricingNote?.remove();
        } else if (!volumePricingNote) {
          newPrice?.insertAdjacentElement(
            'afterend',
            newNote.cloneNode(true)
          );
        } else {
          volumePricingNote.replaceWith(newNote);
        }

        const installmentsInput =
          this.querySelector(
            `#product-form-installment-${this.dataset.blockId} input[name="id"]`
          );

        if (installmentsInput) {
          installmentsInput.value = detail.resource?.id ?? '';
          installmentsInput.dispatchEvent(
            new Event('change', { bubbles: true })
          );
        }

        /*
         * Variant changed.
         * Reset the base price so the selected insurance
         * is added to the new variant price.
         */
        delete this.dataset.basePrice;

        const selectedInsurance = document.querySelector(
          '[data-insurance-options] input[type="radio"]:checked'
        );

        if (selectedInsurance) {
          setTimeout(() => {
            selectedInsurance.dispatchEvent(
              new Event('change', { bubbles: true })
            );
          }, 50);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[product-price] Event promise rejected:',
            error
          );
        }
      });
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}